import { useEffect, useState } from 'react'
import { fetchAllowances, findTokenArgs, type Allowance } from '../lib/allowances.js'

export type AllowancesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; allowances: Allowance[] }
  | { status: 'error'; error: string }

/**
 * Standing allowances from `owner` to `spender` for known tokens named in
 * `data` (see lib/allowances.ts). Idle when disabled or no token is named.
 */
export function useAllowances(
  owner: string | undefined,
  spender: string | undefined,
  data: string | undefined,
  enabled: boolean,
): AllowancesState {
  const [state, setState] = useState<AllowancesState>({ status: 'idle' })
  useEffect(() => {
    const tokens = enabled && data ? findTokenArgs(data) : []
    if (!owner || !spender || tokens.length === 0) {
      setState({ status: 'idle' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    fetchAllowances(owner, spender, tokens).then(
      (allowances) => {
        if (!cancelled) setState({ status: 'ok', allowances })
      },
      (e: unknown) => {
        if (!cancelled)
          setState({ status: 'error', error: e instanceof Error ? e.message : String(e) })
      },
    )
    return () => {
      cancelled = true
    }
  }, [owner, spender, data, enabled])
  return state
}
